/**
 * @file paddlehub 通用图片 demo
 * @author FengGuang(fengguang01@baidu.com)
 */

import React, { useCallback, useRef } from 'react';
import { Button, Checkbox, Col, Collapse, Row, Tooltip, Upload, UploadProps } from 'antd';
import classNames from 'classnames';
import HeightlightBlock from './HeightlightBlock';
import useVideoDemoHooks from './paddleGanDemoHooks';
import { IPaddleGanDemoProps } from './types';
import PCollapse from './PCollapse';
import useMouseDragHook from './components/mouseDragHook';
import useOnResize from './components/onResizeHook';


const PaddleGanDemo: React.FC<IPaddleGanDemoProps> = props => {
    const {
        imgGallery,
        imgGallerySelected,
        imgSelectedItem,
        modelSelect,
        modelSelectStr,
        draggableTipShow,
        hideDraggableTipShow,
        uploadImgErrStr,
        onImgGalleryChange,
        onModelSelectChange,
        onImgUpload
    } = useVideoDemoHooks(props);

    const uploadPictureRequest = useCallback<Exclude<UploadProps['customRequest'], undefined>>(
        (options) => {
            // antd 4.13.1 类型错误
            if ((options.file as File).size < 50 * 1024 * 1024) {
                onImgUpload(options.file as File);
            }
        },
        [onImgUpload]
    );


    const rightImgWrapRef = useRef<HTMLDivElement>(null);
    const [draggableWrapRef, lineRef] = useMouseDragHook<HTMLDivElement>({
        onDrag: (params) => {
            if (lineRef.current && rightImgWrapRef.current) {
                const wrapWidth = draggableWrapRef.current?.offsetWidth ?? 0;
                const left = params.endX - params.startTargetX;
                const left2 = Math.max(5, Math.min(wrapWidth - 5, left));
                const leftPx = `${left2}px`;
                lineRef.current.style.left = leftPx;
                rightImgWrapRef.current.style.left = leftPx;
            }
        }
    });
    const rightImgRef = useRef<HTMLDivElement>(null);
    useOnResize({
        onResize: () => {
            if (draggableWrapRef.current && rightImgRef.current) {
                rightImgRef.current.style.width = `${draggableWrapRef.current.clientWidth}px`;
            }
        }
    });

    const img = imgSelectedItem?.img;
    const resImg = imgSelectedItem?.imgRes?.[modelSelectStr];

    return (
        <div className="old-recovery-demo clear-both">
            <div className="old-recovery-demo-right-background" />
            <div className="old-recovery-demo-right">
                <div className="old-recovery-demo-collapse">
                    <PCollapse
                        defaultActiveKey={['1']}
                        expandIconPosition="right"
                        bordered={false}
                        accordion
                    >
                        <Collapse.Panel
                            key="1"
                            header="选择类型"
                        >
                            <div className="old-recovery-demo-collapse-body">
                                <Checkbox.Group value={modelSelect} onChange={onModelSelectChange}>
                                    <Row>
                                        <Col xs={24}>
                                            <Checkbox value="SuperResolution">超分辨率</Checkbox>
                                        </Col>
                                        <Col xs={24}>
                                            <Checkbox value="Colorization">黑白照片上色</Checkbox>
                                        </Col>
                                    </Row>
                                </Checkbox.Group>
                            </div>
                        </Collapse.Panel>
                        <Collapse.Panel
                            key="2"
                            header="Response"
                        >
                            <div className="old-recovery-demo-collapse-body">
                                <HeightlightBlock
                                    resultJson={imgSelectedItem?.resultJson ?? ''}
                                />
                            </div>
                        </Collapse.Panel>
                    </PCollapse>
                </div>
            </div>
            <div className="old-recovery-demo-main">
                <div
                    className="old-recovery-demo-img"
                    style={{ backgroundImage: img ? `url(${img})` : undefined }}
                    ref={draggableWrapRef}
                >
                    {imgSelectedItem?.key === '' && !!uploadImgErrStr
                        && (
                            <div className="old-recovery-demo-img-uploadimgerr-wrap">
                                <div className="old-recovery-demo-img-uploadimgerr">
                                    <div className="old-recovery-demo-img-uploadimgerr-icon" />
                                    <div className="old-recovery-demo-img-uploadimgerr-text">
                                        {uploadImgErrStr}
                                    </div>
                                </div>
                            </div>
                        )}
                    <div
                        className="old-recovery-demo-img-right-wrap"
                        style={{ display: resImg ? undefined : 'none' }}
                        ref={rightImgWrapRef}
                        onClick={hideDraggableTipShow}
                        onMouseDown={hideDraggableTipShow}
                    >
                        <div
                            ref={rightImgRef}
                            className="old-recovery-demo-img-right"
                            style={{ backgroundImage: resImg ? `url(${resImg})` : undefined }}
                        />
                    </div>
                    {!!imgSelectedItem?.loading
                        && (
                            <div className="old-recovery-demo-img-loading-wrap">
                                <div className="old-recovery-demo-img-loading" />
                            </div>
                        )}
                    <div
                        ref={lineRef}
                        className="old-recovery-demo-img-center-line"
                        onClick={hideDraggableTipShow}
                        style={{ display: resImg ? undefined : 'none' }}
                    >
                        <Tooltip
                            placement="right"
                            title="拖动试试，优化前后差异很大哦"
                            visible={draggableTipShow && (!!resImg)}
                        >
                            <div
                                className="old-recovery-demo-img-center-line-handicon"
                                onClick={hideDraggableTipShow}
                                onMouseDown={hideDraggableTipShow}
                            />
                        </Tooltip>
                    </div>

                    <div
                        className="old-recovery-demo-img-tag old-recovery-demo-img-tag-left"
                        style={{ display: resImg ? undefined : 'none' }}
                    >
                        修复前
                    </div>
                    <div
                        className="old-recovery-demo-img-tag old-recovery-demo-img-tag-right"
                        style={{ display: resImg ? undefined : 'none' }}
                    >
                        修复后
                    </div>
                    <div className="old-recovery-demo-img-input clear-both">
                        <div className="old-recovery-demo-img-input-info">
                            图片文件类型支持PNG、JPG、JPEG、BMP，图片尺寸不超过800*800像素。
                            <div>上PaddleGAN Github官网即可获取老照片和视频修复的完整代码，欢迎大家尝试。</div>
                        </div>

                        <Upload
                            customRequest={uploadPictureRequest}
                            showUploadList={false}
                        >
                            <Button
                                type="primary"
                                size="large"
                                className="old-recovery-demo-img-input-upload"
                                loading={!imgSelectedItem?.key && imgSelectedItem?.loading}
                            >
                                试试你的图片?
                            </Button>
                        </Upload>
                    </div>
                </div>
                <div className="old-recovery-demo-img-gallery">
                    <ul className="old-recovery-demo-img-gallery-item-wrap">
                        {imgGallery.map(item => (
                            <li
                                key={item.img}
                                className={classNames(
                                    'old-recovery-demo-img-gallery-item',
                                    {
                                        'old-recovery-demo-img-gallery-item-selected': item.img === imgGallerySelected
                                    }
                                )}
                                style={{ backgroundImage: `url(${item.img})` }}
                                onClick={() => onImgGalleryChange(item.img)}
                            >
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default PaddleGanDemo;
